/**
 * A ponte entre o simulador portado e o motor extraído.
 *
 * PARA QUE ISTO EXISTE
 * O `engine/` mudou **assinatura** de 7 funções ao ser extraído — não o cálculo, só a
 * forma de chamar: o que antes era lido de uma variável global agora entra por parâmetro.
 * Havia dois caminhos:
 *   (a) editar as ~50 chamadas dentro de `simulador.js`, ou
 *   (b) uma ponte que expõe o motor com a assinatura ANTIGA.
 * Escolhi (b). O porte pede que `simulador.js` fique o mais literal possível em relação à
 * v67 — cada chamada editada é uma chance de errar um argumento em silêncio, e o diff
 * contra `legado-v67/app.js` deixaria de ser legível. Com a ponte, **nenhuma chamada muda**.
 *
 * O PREÇO, DITO SEM ENFEITE
 * A ponte reintroduz de propósito o acoplamento que a extração tirou: ela lê o estado
 * global do simulador por meio dos acessores injetados abaixo. Isso é aceitável aqui e
 * não seria no `engine/` — o motor continua puro, testável e provado pelos 68 casos de
 * referência. A sujeira fica confinada a este arquivo, que é pequeno e está à vista.
 *
 * ⚠ Este arquivo é TypeScript de propósito, apesar de `simulador.js` ser JavaScript. É
 * exatamente aqui que um argumento trocado passaria despercebido, e é o compilador que
 * impede isso.
 */
import * as E from '../../../../engine/index';
import type { Empreendimento, Lev, Unidade, Vaga } from '../../../../engine/index';

/* ============================================================================
 * O que a ponte precisa saber do simulador
 * ========================================================================== */

export interface ContextoPonte {
  /** A tabela de premissas macro. Na v67 era a global `PREMISSAS`. */
  premissas(): E.PremissasMacro;
  /** O `lev` do cenário em edição. Na v67, `estado.rascunho.lev`. */
  lev(): Lev;
  /** Unidades da tabela `'det'` ou `'dem'`. Na v67, `unitsKeyArr(key)`. */
  unidades(chave: string): Unidade[];
  /** Vagas da tabela `'det'` ou `'dem'`. Na v67, `vagasKeyArr(key)`. */
  vagas(chave: string): Vaga[];
}

let ctx: ContextoPonte | null = null;

/** Chamada uma vez, no arranque do simulador, antes de qualquer cálculo. */
export function configurarPonte(c: ContextoPonte): void {
  ctx = c;
}

export function pontePronta(): boolean {
  return ctx !== null;
}

function exigir(): ContextoPonte {
  if (ctx === null) {
    /* Erro alto de propósito: sem a ponte configurada, o cálculo sairia com premissas
       vazias e a tela mostraria números plausíveis e errados. Melhor quebrar. */
    throw new Error('A ponte do motor não foi configurada — chame configurarPonte() no arranque.');
  }
  return ctx;
}

/* ============================================================================
 * AS 5 QUE PRECISAM DE ADAPTAÇÃO
 * Assinatura à esquerda = a da v67. Corpo à direita = a do engine.
 * ========================================================================== */

/** v67: `calcBase(b, lev)` — lia a global `PREMISSAS`. */
export const calcBase = (b: Empreendimento, lev: Lev): E.ResultadoBase =>
  E.calcBase(b, lev, exigir().premissas());

/** v67: `demDerivar()` — lia `estado.rascunho.lev`. */
export const demDerivar = (): E.DerivadosDemanda => E.demDerivar(exigir().lev());

/** v67: `fracaoTotal(key)` — resolvia a chave de UI para as duas listas. */
export const fracaoTotal = (chave: string): number =>
  E.fracaoTotal(exigir().unidades(chave), exigir().vagas(chave));

/** v67: `faixaLGC(r, f)` — lia a global `PREMISSAS`. */
export const faixaLGC = (r: E.Rating, f: E.Funding): [number, number] =>
  E.faixaLGC(exigir().premissas(), r, f);

/** v67: `sugFin(preco)` — lia `PREMISSAS.perfil`. */
export const sugFin = (preco: number): number => E.sugFin(exigir().premissas().perfil, preco);

/* ============================================================================
 * AS TAXAS DE JUROS DO FLUXO — Fluxo pelo memorial (15/10/2026)
 *
 * `calcFluxo` virou ADAPTADA: o simulador continua chamando com 3 argumentos (como na v67, e
 * tests/porte-literal o exige), e a ponte injeta as séries de juros (Banco Central + projeção)
 * no 4º. Quem entrega as taxas é a CASCA (montagem.ts), antes de carregar o simulador — elas
 * vêm do banco, que o simulador não conhece. Sem taxas, `null`: a linha "Despesas Financeiras"
 * avisa (`jurosAviso`) em vez de inventar número.
 * ========================================================================== */

let taxasDoFluxo: E.TaxasMensais | null = null;

/** Chamada pela casca (montagem.ts) a cada montagem do simulador. */
export function configurarTaxas(t: E.TaxasMensais | null): void {
  taxasDoFluxo = t;
}

/** v67: `calcFluxo(b, lev, c)` — as 3 constantes provisórias eram fixas; agora as taxas vêm da casca. */
export const calcFluxo = (b: Empreendimento, lev: Lev, c: E.ResultadoBase): E.ResultadoFluxo | null =>
  E.calcFluxo(b, lev, c, { ...E.CONFIG_FLUXO_PADRAO, taxas: taxasDoFluxo });

/* ============================================================================
 * AS QUE PASSAM DIRETO
 *
 * `calcViab` entra aqui, e não acima, porque o parâmetro que o engine acrescentou tem
 * **valor padrão igual ao da v67** (`FORMATADORES_PADRAO`): chamar com 4 e com 5 argumentos
 * dá o mesmo resultado.
 * ========================================================================== */

export {
  /* datas e arredondamento */
  addDays, teto, ymFromParts, ymLabel, ymOf,
  /* formatação — `fmtPc` e `fmtMoeda` entram aqui porque o simulador os usa 14 e 5 vezes
     (matriz de Obras e células de valor), e eles tinham ficado fora do índice do motor */
  fmtBRL, fmtMoeda, fmtNum2, fmtPc, moeda, moeda0, num, parseNum, pct, pct1,
  /* demanda por unidade (EL-154) */
  _avalEfet, _fracEfet, _ingressaValida, _saiuEstoqueDem, _unitExcl, _vagaExcl, _vagasVinc,
  demDemanda, demTemDados,
  /* lote 2 de 09/10/2026, item 26: o Detalhamento "zerado" decide o caminho das barras do Fluxo
     (média × unidade a unidade). Passa direto como todo export do motor; desde a revisão do lote 2
     (10/10/2026) o aviso do Espelhar o lê, para dizer qual caminho vale pelo MESMO predicado */
  vendasDetZeradas,
  /* decisão do usuário de 11/10/2026 ("O campo da demanda"): sem nenhuma "Vendas Fin." nas Premissas Mensais, a demanda
     do Fluxo mensal é o campo "Unidades financiadas na demanda" × a média. O simulador não a lê; passa direto para as
     ferramentas de validação conferirem a tela pelo MESMO predicado do `calcFluxo` */
  premissasMensaisSemVendasFin,
  /* base de cálculo (EL-191, 08/10/2026): a coluna "Base de cálculo" do EL-154/EL-147
     mostra o que o motor escolheu, e a memória de cálculo lê o FSF linha a linha */
  baseDaVaga, baseDem, baseDet, baseDoPacote,
  /* garantias — `terrenoDetalhe` (09/10/2026, item 28): a memória do Terreno lê o motor linha a linha */
  estoqueDemReal, fsfDetalhe, fsfPorUnidade, repasseDaUnidade, terrenoDetalhe, terrenoGarFrac, terrenoGarM2,
  /* núcleo */
  calcEstatico, calcViab,
  /* Fluxo pelo memorial (15/10/2026): o simulador não os chama; passam direto para a memória de
     cálculo e as ferramentas de validação lerem a MESMA regra do `calcFluxo` */
  diasUteis, primeiroDia, dataDaDemanda, ighDaMedicao, unidadesDoFluxo, jurosDoMes, paramJurosDe, taxaDoMes, estoqueNoMes,
  /* Receitas Financeiras = a remuneração das unidades (16/10/2026) */
  aliquotaIR, diasEntre, fatorPoupancaTR, medicoesDoFluxo, remuneracaoDaUnidade, taxaRemuneracao,
  /* vendas "um caminho OU outro" (20/10/2026): a tela mostra a contagem que o cálculo usa */
  vendasMensais,
  /* IGH — `_saiuEstoqueTempo` e `ymRegistroUnidade` entraram com a Parte B (29/07/2026):
     é a tela que explica ao usuário em que mês cada unidade sai do estoque */
  _saiuEstoqueTempo, calcIGH, ighAvalia, ighSerie, medAcumAt, serieGarantias, ymIGH,
  ymRegistroUnidade,
  /* lote 2 de 09/10/2026, item 20: a "Data de financiamento" que vale — a do "Cumprimento de
     demanda" para quem ingressa. A célula travada do EL-147, a exportação e o nº de meses do
     Premissas Mensais leem a MESMA função que o IGH mês a mês */
  dataFinanciamentoEfetiva,
  /* lote 3 de 09/10/2026 (itens 7 e 29): a memória de cálculo das barras do Fluxo (EL-127/EL-111) lê
     as parcelas da barra pela MESMA função que a produziu (`_barraDoMes`) — unidade a unidade */
  barraDetalhe,
  /* lote 4 de 10/10/2026 (item 1): o mês a partir do qual o "IGH pós-demanda" olha — a linha do EL-111,
     a memória de cálculo e o gráfico do EL-078 leem a MESMA regra do `ighAvalia` */
  ighPosDemandaDesde,
  /* constante do IGH */
  IGH_COEF_EXEMPLO,
} from '../../../../engine/index';
